import React, { useState } from 'react';
import { Shield, Key, Copy, Download } from 'lucide-react';
import  Card  from '@/components/ui/Card';
import  Button  from '@/components/ui/Button';
import  Input  from '@/components/ui/Input';
import { Alert, AlertDescription } from '@/components/ui/Alert';

const MFASetup = () => {
  const [step, setStep] = useState('initial'); // initial, setup, verify
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [token, setToken] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const startSetup = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/mfa/setup', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message);

      setQrCode(data.qrCode);
      setSecret(data.secret);
      setStep('setup');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnable = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/mfa/verify', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message);

      setBackupCodes(data.backupCodes);
      setStep('verify');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadBackupCodes = () => {
    const content = backupCodes.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'localflow-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold flex items-center">
          <Shield className="mr-2" /> Two-Factor Authentication
        </h2>
        <p className="text-gray-600 mt-1">
          Enhance your account security with two-factor authentication
        </p>
      </div>

      {step === 'initial' && (
        <div>
          <p className="mb-4">
            Two-factor authentication adds an extra layer of security to your account.
            When enabled, you'll need to enter both your password and a code from your
            authenticator app when signing in.
          </p>
          <Button onClick={startSetup} disabled={loading}>
            {loading ? 'Setting up...' : 'Set up 2FA'}
          </Button>
        </div>
      )}

      {step === 'setup' && (
        <div className="space-y-6">
          <div>
            <h3 className="font-medium mb-2">1. Scan QR Code</h3>
            <div className="bg-white p-4 inline-block rounded-lg border">
              <img src={qrCode} alt="QR Code" className="w-48 h-48" />
            </div>
          </div>

          <div>
            <h3 className="font-medium mb-2">2. Manual Entry</h3>
            <div className="flex items-center space-x-2">
              <code className="bg-gray-100 px-3 py-2 rounded">{secret}</code>
              <Button variant="outline" size="sm" onClick={() => copyToClipboard(secret)}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div>
            <h3 className="font-medium mb-2">3. Enter Verification Code</h3>
            <div className="space-y-4">
              <Input
                type="text"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Enter 6-digit code"
                className="max-w-xs"
              />
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <Button onClick={verifyAndEnable} disabled={loading}>
                {loading ? 'Verifying...' : 'Verify & Enable 2FA'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {step === 'verify' && (
        <div className="space-y-6">
          <Alert>
            <AlertDescription>
              2FA has been successfully enabled! Save your backup codes in a secure location.
            </AlertDescription>
          </Alert>

          <div>
            <h3 className="font-medium mb-2">Backup Codes</h3>
            <div className="bg-gray-100 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, index) => (
                  <code key={index} className="font-mono">{code}</code>
                ))}
              </div>
            </div>
            <div className="mt-4 flex space-x-4">
              <Button variant="outline" onClick={downloadBackupCodes}>
                <Download className="w-4 h-4 mr-2" />
                Download Codes
              </Button>
              <Button
                variant="outline"
                onClick={() => copyToClipboard(backupCodes.join('\n'))}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Codes
              </Button>
            </div>
          </div>

          <Button onClick={() => window.location.reload()}>
            Complete Setup
          </Button>
        </div>
      )}
    </Card>
  );
};

export default MFASetup;